import { HELP_BASE_LANGUAGE, HELP_LANGUAGES, type HelpLanguage } from './types';

const STORAGE_PREFIX = 'glossa.help';

/**
 * Whether the manual also shows a floating "?" over the page.
 *
 * Off by default. The floating button lived in the bottom-right corner, which on every
 * list page is exactly where the table keeps its pagination arrows and row actions — it
 * covered them. The docked "?" in the top bar is always reachable and covers nothing, so
 * the floating one is now a preference for people who want it back.
 *
 * localStorage rather than a global-state atom on purpose: this is one component's own chrome,
 * not app state, and `help/` stays importable with no providers around it (see the note
 * at the top of HelpLauncher).
 */
export const HELP_FLOATING_KEY = `${STORAGE_PREFIX}.floating-button`;

export const readFloatingPref = (): boolean => {
  try {
    return window.localStorage.getItem(HELP_FLOATING_KEY) === 'on';
  } catch {
    // Storage can throw outright (private mode, blocked site data) — fall back to the default.
    return false;
  }
};

export const writeFloatingPref = (on: boolean): void => {
  try {
    window.localStorage.setItem(HELP_FLOATING_KEY, on ? 'on' : 'off');
  } catch {
    // Best-effort: the choice then simply lasts for this session.
  }
};

/**
 * Which language the guide answers in.
 *
 * Same reasoning as the floating button — one component's own chrome, kept in localStorage so a
 * user does not re-pick their language on every visit. Validated on read against the language
 * list, so adding a language needs no change here and a stale value cannot select a language
 * the guides were never written in.
 */
export const HELP_LANGUAGE_KEY = `${STORAGE_PREFIX}.language`;

export const readLanguagePref = (): HelpLanguage => {
  try {
    const stored = window.localStorage.getItem(HELP_LANGUAGE_KEY);
    const match = HELP_LANGUAGES.find((code) => code === stored);
    return match ?? HELP_BASE_LANGUAGE;
  } catch {
    return HELP_BASE_LANGUAGE;
  }
};

export const writeLanguagePref = (language: HelpLanguage): void => {
  try {
    window.localStorage.setItem(HELP_LANGUAGE_KEY, language);
  } catch {
    // Best-effort: the choice then lasts for this session only.
  }
};
